import { Checkbox, Chip, LinearProgress, TextField } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { FixedSizeList, ListChildComponentProps } from 'react-window'

export interface SelectionItem  {
    value: string;
    label: string;
    disabled?: boolean;
}

export interface SIAutocompleteProps {
    items: SelectionItem[]
    initial_selection?: SelectionItem
    onSelect: (item?: SelectionItem) => void
    onAddSelectionItem?: (item: SelectionItem) => void
    verifyAdd?: (item: SelectionItem) => boolean
    style?: React.CSSProperties
    disableSpecialCharacters?: boolean,
    canAdd?: () => boolean,
    loadProgress?: () => boolean,
    disabled?: boolean
    placeholder?: string
    variant?: 'outlined' | 'standard' | 'filled'
}


const LISTBOX_PADDING = 8
const LISTBOX_ITEM_SIZE = 36
const LISTBOX_VISIBLE_ROWS = 8
const LISTBOX_HEIGHT = LISTBOX_VISIBLE_ROWS * LISTBOX_ITEM_SIZE + LISTBOX_PADDING * 2

const OuterElementContext = React.createContext({})
const OuterElementType = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => {
    const outerProps = React.useContext(OuterElementContext)
    return <div ref={ref} {...props} {...outerProps} />
})
OuterElementType.displayName = 'OuterElementType'

function VirtualizedListbox(
    props: React.HTMLAttributes<HTMLDivElement> & { children?: React.ReactNode },
    _ref: React.Ref<HTMLDivElement>
) {
    const { children, ...other } = props
    const itemData = React.Children.toArray(children)
    const itemCount = itemData.length
    const getItemSize = () => LISTBOX_ITEM_SIZE
    const getHeight = () => {
        if (itemCount > LISTBOX_VISIBLE_ROWS) {
            return LISTBOX_HEIGHT
        }
        return itemCount * getItemSize() + LISTBOX_PADDING * 2
    }
    return (
        <OuterElementContext.Provider value={other}>
                <FixedSizeList
                    itemData={itemData}
                    height={getHeight()}
                    width="100%"
                    outerElementType={OuterElementType}
                    itemSize={getItemSize()}
                    overscanCount={5}
                    itemCount={itemCount}
                    style={{ listStyle: 'none', margin: 0, padding: LISTBOX_PADDING, boxSizing: 'border-box' }}
                >
                {({ data, index, style }: ListChildComponentProps<React.ReactElement[]>) => {
                    const child = data[index]
                    return React.isValidElement(child)
                        ? React.cloneElement(child, {
                              style: { ...style, top: (style.top as number) + LISTBOX_PADDING },
                          } as React.HTMLAttributes<HTMLElement>)
                        : null
                }}
            </FixedSizeList>
        </OuterElementContext.Provider>
    )
}
const VirtualizedListboxComponent = React.forwardRef(VirtualizedListbox) as React.ComponentType<
    React.HTMLAttributes<HTMLDivElement> & { children?: React.ReactNode }
>

export const SIAutocomplete = ({ items, initial_selection, onSelect, onAddSelectionItem, verifyAdd,  style, disableSpecialCharacters, canAdd, loadProgress, disabled, placeholder, variant='outlined'}: SIAutocompleteProps) => {
    const [selectedItem, setSelectedItem] = useState<SelectionItem | undefined>(initial_selection)
    const [error, setError] = useState<boolean>(false)

    const setSelectItem = (item?: SelectionItem ) => {
        setSelectedItem(item)
        onSelect(item)
    }

    return <Autocomplete
        value={selectedItem ?? null}
        disabled={disabled}
        onChange={(event, item) => {
            if(error || !item) {
                setSelectItem()
            } else if (typeof item !== 'string') {
                if (onAddSelectionItem && item.label.startsWith('Add ')){
                    item.label = item.value
                    onAddSelectionItem(item)
                }
                setSelectItem(item)
            }
        }}
        filterOptions={(options, params) => {
            const filtered = createFilterOptions<SelectionItem>({stringify: (option: SelectionItem) => option.label})(options, params)

            const regex = /^[\p{L}\p{N} !\-_.*/'()+]*$/u
            const isInputValid = params.inputValue.match(regex)

            // Suggest the creation of a new value
            if (isInputValid && canAdd && canAdd() && onAddSelectionItem && params.inputValue !== '' && !error) {
                const newItem = {
                    value: params.inputValue,
                    label: `Add "${params.inputValue}"`,
                }
                if (!verifyAdd || verifyAdd(newItem)) filtered.push(newItem)
            }

            return filtered
        }}
        ListboxComponent={VirtualizedListboxComponent}
        disableListWrap
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={items}
        getOptionLabel={(option) => option ? option.label || '' : ''}
        getOptionDisabled={(option) => option.disabled || false}
        renderOption={(option) => (
            <span style={{ 
                color: option.disabled ? '#666' : 'inherit',
                opacity: option.disabled ? 0.6 : 1
            }}>
                {option.label}
            </span>
        )}
        style={style}
        freeSolo
        renderInput={params => (
            items.length || !loadProgress || !loadProgress() ? 
            <TextField
                {...params}
                variant={variant}
                InputProps={{
                    ...params.InputProps,
                    ...(variant === 'standard' ?{ disableUnderline: true } : {})
                  }}
                error={error}
                placeholder={placeholder ?? undefined}
                onChange={ (event) =>{
                    if (disableSpecialCharacters) {
                        const inputValue = event.target.value
                        const regex = /^[\p{L}\p{N} !\-_.*/'()+]*$/u
                        const isInputValid = inputValue.match(regex)
                        if(!inputValue || isInputValid){
                            setError(false)
                        } else if (inputValue && !isInputValid) {
                            setError(true)
                        }
                    }}
                }
                onBlur={() => setError(false)}
                />
            :
            <div>
                <TextField {...params} label="Loading" variant="outlined" />
                <LinearProgress/>
            </div>
        )}
    />
}

export interface SIAutocompleteMultiSelectProps {
    items: SelectionItem[],
    initial_selection?: SelectionItem[],
    limitTags?: number,
    onSelect: (item?: SelectionItem[]) => void,
    style?: React.CSSProperties,
    loadProgress?: () => boolean,
    disabled?: boolean,
    pageSize?: number
}

const LOAD_MORE_VALUE = '__LOAD_MORE__';
const TOO_MANY_RESULTS_VALUE = '__TOO_MANY_RESULTS__';

export const SIAutocompleteMultiSelect = ({items, initial_selection, limitTags, onSelect, style, loadProgress, disabled, pageSize = 100}: SIAutocompleteMultiSelectProps) => {
    const [selectedItems, setSelectedItems] = useState<SelectionItem[]>([]);
    const [open, setOpen] = useState(false);
    const [displayLimit, setDisplayLimit] = useState(pageSize);

    const isAllSelected = items.length === selectedItems.length;
    const limitTagsVal = limitTags ?? 5;
    
    useEffect(() => {
        if (disabled) {
            setOpen(false);
        }
    }, [disabled]);

    useEffect(() => {
        if (initial_selection ) {
            if (JSON.stringify(initial_selection) !== JSON.stringify(selectedItems)) {
                setSelectedItems(initial_selection)
            }
        }
    }, [initial_selection]);

    const setSelectItems = (items: SelectionItem[] ) => {
        setSelectedItems(items)
        onSelect(items)
    }

    const handleToggleSelectAll = (isSelected: Boolean) => {
        setSelectItems(isSelected ? items : [])
    };

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;
    const getOptionSelected = (option: SelectionItem, anotherOption: SelectionItem) => option.value === anotherOption.value;
    const filter = createFilterOptions();

    const selectAllLabel = 'Select All'
    return <Autocomplete
                open={open}
                onOpen={() => !disabled && setOpen(true)}
                onClose={() => {
                    setOpen(false);
                    // Reset display limit to first page when closing
                    setDisplayLimit(pageSize);
                }}
                disabled={disabled || !items.length}
                multiple
                id="checkboxes"
                limitTags={limitTagsVal}
                options={items}
                disableCloseOnSelect
                value={selectedItems}
                onChange={(e, options, reason) => {
                    if (reason === "select-option" || reason === "remove-option") {
                        // Check if "Load More" was clicked
                        const loadMoreOption = options.find(option => option.value === LOAD_MORE_VALUE);
                        if (loadMoreOption) {
                            // Don't select it, just load more items
                            setDisplayLimit(prev => prev + pageSize);
                            return;
                        }
                        
                        // Ignore the "Too Many Results" message (shouldn't be selectable anyway)
                        const tooManyResultsOption = options.find(option => option.value === TOO_MANY_RESULTS_VALUE);
                        if (tooManyResultsOption) {
                            return;
                        }
                        
                        if (options.find(option => option.label === selectAllLabel)) {
                            handleToggleSelectAll(!isAllSelected);
                        } else {
                            setSelectItems(options);
                        }
                    } else if (reason === "clear") {
                        setSelectItems([]);
                    }
                }}
                getOptionSelected={getOptionSelected}
                getOptionLabel={option => option.label}
                getOptionDisabled={(option) => option.value === TOO_MANY_RESULTS_VALUE}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params as any) as any;
                    
                    // Always limit results for performance
                    let limitedFiltered = filtered;
                    let hasMore = false;
                    const maxResults = params.inputValue ? 500 : displayLimit; // Show more when searching, but still limit
                    
                    if (filtered.length > maxResults) {
                        // Ensure selected items are always included
                        const selectedValues = new Set(selectedItems.map(item => item.value));
                        const selectedFromFiltered = filtered.filter((option: SelectionItem) => 
                            selectedValues.has(option.value)
                        );
                        const notSelected = filtered.filter((option: SelectionItem) => 
                            !selectedValues.has(option.value)
                        );
                        
                        // Take maxResults items, prioritizing selected ones
                        limitedFiltered = [
                            ...selectedFromFiltered,
                            ...notSelected.slice(0, maxResults - selectedFromFiltered.length)
                        ];
                        hasMore = notSelected.length > (maxResults - selectedFromFiltered.length);
                    }
                    
                    // Add Select All at the beginning (only when not searching)
                    let result = (selectAllLabel && !params.inputValue) ? [{ value: selectAllLabel, label: selectAllLabel }, ...limitedFiltered] : [...limitedFiltered];
                    
                    // Add Load More or "Too Many Results" message at the end
                    if (hasMore) {
                        const remainingCount = filtered.length - limitedFiltered.length;
                        
                        if (params.inputValue) {
                            // When searching/filtering, show a message to refine search instead of Load More
                            result.push({ 
                                value: TOO_MANY_RESULTS_VALUE, 
                                label: `Showing first 500 of ${filtered.length} matches. Please refine your search.`
                            });
                        } else {
                            // When not searching, show Load More button
                            result.push({ 
                                value: LOAD_MORE_VALUE, 
                                label: `Load More... (${remainingCount} more items)`
                            });
                        }
                    }
                    
                    return result;
                }}
                renderOption={(option, { selected }) => {
                    // Special rendering for "Load More" option
                    if (option.value === LOAD_MORE_VALUE) {
                        return (
                            <div style={{ 
                                width: '100%',
                                padding: '8px',
                                cursor: 'pointer',
                            }}>
                                <Chip 
                                    label={option.label}
                                    size="small"
                                    style={{
                                        backgroundColor: '#e3f2fd',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        width: '100%',
                                        justifyContent: 'center'
                                    }}
                                />
                            </div>
                        );
                    }
                    
                    // Special rendering for "Too Many Results" message
                    if (option.value === TOO_MANY_RESULTS_VALUE) {
                        return (
                            <div style={{ 
                                width: '100%',
                                padding: '8px',
                                cursor: 'default',
                                pointerEvents: 'none'
                            }}>
                                <Chip 
                                    label={option.label}
                                    size="small"
                                    style={{ 
                                        backgroundColor: '#ff9800',
                                        fontWeight: 500,
                                        width: '100%',
                                        justifyContent: 'center'
                                    }}
                                />
                            </div>
                        );
                    }
                    
                    const selectAllProps =
                        option.label === selectAllLabel
                            ? { checked: isAllSelected }
                            : {};

                    return (
                        <React.Fragment>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                                {...selectAllProps}
                            />
                            {option.label}
                        </React.Fragment>
                    );
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                style={style}
                renderTags={(value, getTagProps) => {
                    const numTags = value.length;
            
                    return (
                      <>
                        {value.slice(0, limitTagsVal).map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.value}
                            label={option.label}
                          />
                        ))}
            
                        {numTags > limitTagsVal && ` +${numTags - limitTagsVal}`}
                      </>
                    );
                  }}
                renderInput={params => (
                    items.length || !loadProgress || !loadProgress() ? 
                    <TextField {...params} variant="outlined" /> :
                    <div>
                        <TextField {...params} label="Loading" variant="outlined" />
                        <LinearProgress/>
                    </div>
                )}
            />  
}